// "Eligible Funding Sources by Goal" mapping guide. Reads goalConstraints (getgoeconfig) and
// renders the goalType/duration/pre-post constraint mapping as a read-only reference table.
import { useMemo } from 'react';
import { styled, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../common/hooks';
import { Icon } from '../../components/atoms';
import { PopUpModal } from '../../components/molecules/PopUpModal/PopUpModal';
import { TableWithCustomRender } from '../../components/molecules/TableWithCustomRender';
import { GoalTypeBadge } from './GoalTypeTiles';
import { GoalRowType } from './goalInlineHelpers';

// goalConstraints pre/post values can arrive as a pipe/comma-separated string, a string[], or
// an { accountTypeOrder: string[] } object; normalize any of these to a single ' | ' joined label.
// Each token is '<value>-<duration>'; keep the value part and resolve it to a localized label.
const identityLabel = (value: string): string => value;
const formatConstraint = (value: unknown, resolveLabel: typeof identityLabel = identityLabel): string => {
    if (value == null) return '-';
    const parts = Array.isArray(value)
        ? value
        : Array.isArray((value as { accountTypeOrder?: string[] })?.accountTypeOrder)
        ? (value as { accountTypeOrder: string[] }).accountTypeOrder
        : String(value).split(/[|,]/);
    const cleaned = parts.map((p) => resolveLabel(String(p).split('-')[0].trim())).filter(Boolean);
    return cleaned.length ? cleaned.join(' | ') : '-';
};

// goalType arrives as '<type>-<duration>' (e.g. 'wealth-Short'); split into its two parts.
const splitGoalType = (goalType?: string): { type: string; duration: string } => {
    const [type = '', duration = ''] = (goalType ?? '').split('-');
    return { type: type.trim(), duration: duration.trim() };
};

// Lowercase goalType token -> translation key for its label (rendered as '<Type> Goal').
const GOAL_TYPE_LABEL_KEY: Record<string, string> = {
    wealth: 'WEALTH_GOAL_CARD_HEADER',
    income: 'INCOME_GOAL_CARD_HEADER',
    retirement: 'RETIREMENT_GOAL_CARD_HEADER'
};

// Duration token -> chip label key. Chip colors are resolved from theme.palette.semantic.bd/bg
// (keyed by the same short/medium/long token) inside the component.
const DURATION_META: Record<'short' | 'medium' | 'long', { labelKey: string }> = {
    short: { labelKey: 'SHORT' },
    medium: { labelKey: 'MEDIUM' },
    long: { labelKey: 'LONG' }
};

const durationKey = (duration: string): keyof typeof DURATION_META | '' => {
    const d = duration.toLowerCase();
    if (d.startsWith('short')) return 'short';
    if (d.startsWith('medium')) return 'medium';
    if (d.startsWith('long')) return 'long';
    return '';
};

const DurationChip = styled('span')<{ $border: string; $background: string }>(({ theme, $border, $background }) => ({
    display: 'flex',
    width: '94px',
    height: '24px',
    padding: '4px 12px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '100px',
    border: `1px solid ${$border}`,
    background: $background,
    color: theme.palette.semantic.text.default,
    fontFamily: '"TT Commons Pro"',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '17px',
    whiteSpace: 'nowrap'
}));

const InfoRow = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
    fontSize: 14,
    lineHeight: '19px',
    color: theme.palette.semantic.text.default,
    fontFamily: '"TT Commons Pro"',
    fontStyle: 'italic',
    fontWeight: 400,
    letterSpacing: '0',
    '& .info-icon': {
        '--info-icon-fg': `${theme.palette.semantic.icon.info}`
    }
}));

const SubtitleRow = styled('div')(({ theme }) => ({
    color: theme.palette.semantic.text.default,
    fontFamily: '"TT Commons Pro"',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '22px',
    marginTop: 10,
    marginBottom: 24
}));

type Props = {
    open: boolean;
    onClose: () => void;
};

export const EligibleFundingSourcesModal = ({ open, onClose }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const language = useAppSelector((state) => state?.global?.currentLanguage);
    const goalConstraints = useAppSelector((state) => state?.global?.goeConfig?.userLevelConfig?.goalConstraints);
    const investmentAccountType = useAppSelector((state) => state?.global?.orgInfo?.investmentAccountType);
    const incomeSourceType = useAppSelector((state) => state?.global?.orgInfo?.incomeSourceType);

    const headers = useMemo(
        () => [
            {
                label: t('MAPPING_GUIDE_COL_GOAL_TYPE'),
                key: 'goalType',
                align: 'left' as const,
                width: '25%',
                render: (value: string, row: { typeRaw?: string }) => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                        {row?.typeRaw && <GoalTypeBadge type={row.typeRaw as GoalRowType} />}
                        {value}
                    </span>
                )
            },
            {
                label: t('DURATION'),
                key: 'duration',
                align: 'left' as const,
                width: '15%',
                render: (value: string) => {
                    const key = durationKey(value);
                    const meta = key ? DURATION_META[key] : undefined;
                    return meta ? (
                        <DurationChip
                            $border={theme.palette.semantic.bd[key as 'short' | 'medium' | 'long']}
                            $background={theme.palette.semantic.bg[key as 'short' | 'medium' | 'long']}
                        >
                            {t(meta.labelKey)}
                        </DurationChip>
                    ) : (
                        value || '-'
                    );
                }
            },
            {
                label: t('MAPPING_GUIDE_COL_PRE_RETIREMENT'),
                key: 'preRetirement',
                align: 'left' as const,
                width: '27.5%'
            },
            {
                label: t('MAPPING_GUIDE_COL_POST_RETIREMENT'),
                key: 'postRetirement',
                align: 'left' as const,
                width: '27.5%'
            }
        ],
        [t, theme]
    );

    const rows = useMemo(() => {
        const lang = language ?? 'en';
        // Both lists are keyed by language; label holds the translated account/source name.
        const sourceList = [
            ...(investmentAccountType?.[lang] ?? investmentAccountType?.en ?? []),
            ...(incomeSourceType?.[lang] ?? incomeSourceType?.en ?? [])
        ] as Array<{ value: string; label: string }>;
        const resolveLabel = (value: string) => sourceList.find((s) => s?.value === value)?.label ?? value;
        const resolveGoalTypeLabel = (type: string) => {
            const key = GOAL_TYPE_LABEL_KEY[type.toLowerCase()];
            return key ? t(key) : type || '-';
        };
        return (goalConstraints ?? []).map((item, index) => {
            const { type, duration } = splitGoalType(item?.goalType);
            return {
                id: item?.goalType ?? index,
                typeRaw: type.toLowerCase(),
                goalType: resolveGoalTypeLabel(type),
                duration,
                preRetirement: formatConstraint(item?.preRetirementConstraints, resolveLabel),
                postRetirement: formatConstraint(item?.postRetirementConstraints, resolveLabel)
            };
        });
    }, [goalConstraints, investmentAccountType, incomeSourceType, language, t]);

    if (!open) return null;

    return (
        <PopUpModal
            open
            width="1038px"
            PaperProps={{
                sx: {
                    padding: '32px',
                    maxHeight: '600px',
                    '& .MuiDialogTitle-root': {
                        padding: 0,
                        color: theme.palette.semantic.bd.brandDark,
                        fontFeatureSettings: "'liga' off",
                        fontFamily: '"TT Commons Pro"',
                        fontSize: '24px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: '30px'
                    },
                    '& .MuiDialogContent-root': { padding: 0 },
                    // align the close button on the same row as the h2 title
                    '& > .MuiButtonBase-root': { padding: '32px', top: '4px' },
                    '& .MuiDialogContent-dividers': { border: '0' },
                    '& .MuiDialogActions-root': { display: 'none' },
                    '& .MuiTableCell-body': {
                        color: theme.palette.semantic.int.selected,
                        fontFamily: '"TT Commons Pro"',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '22px' /* 137.5% */,
                        verticalAlign: 'top',
                        padding: '12px 8px'
                    }
                }
            }}
            title={t('ELIGIBLE_FUNDING_SOURCES_BY_GOAL_TITLE')}
            description={
                <>
                    <SubtitleRow>{t('ELIGIBLE_FUNDING_SOURCES_BY_GOAL_SUBTITLE')}</SubtitleRow>
                    <InfoRow>
                        <Icon
                            id="info-icon-circle"
                            size={16}
                            fill={theme.palette.semantic.bg.info}
                            className="info-icon"
                        />
                        {t('ELIGIBLE_FUNDING_SOURCES_INFO_TEXT')}
                    </InfoRow>
                    <TableWithCustomRender headers={headers} dataSource={rows} showExpandButton={false} />
                </>
            }
            showCancelButton={false}
            saveButtonLabel={t('CLOSE_LABEL')}
            handleClose={onClose}
            handleSaveButtonAction={onClose}
        />
    );
};

export default EligibleFundingSourcesModal;
